import { takeLatest, put, call, select } from "redux-saga/effects";
import api from 'api/client';
import i18next from 'i18next';
import { resolveError } from 'utils/resolveError';
import * as client from 'reducer/rClient';
import * as message from "reducer/rMessageBox";
import { fetchClientRequest } from "reducer/rClient";

export function* sClient() {
    // 고객사 정보 조회 (페이징)
    yield takeLatest(client.fetchClientRequest, fetchClientData);
    // 고객사 정보 조회 (리스트)
    yield takeLatest(client.fetchClientListRequest, fetchClientDataList);
    // 고객사 등록
    yield takeLatest(client.fetchAddClientRequest, fetchAddClient);
    // 고객사 수정 (IS_DISPLAY 토글)
    yield takeLatest(client.fetchUpdateClientRequest, fetchUpdateClient);
    // WEBCOM-5519: Partner 메타 수정
    yield takeLatest(client.fetchUpdateClientMetaRequest, fetchUpdateClientMeta);
}

function* fetchClientData(action) {
    try {
        const { limit, offset, keyword, sortBy, sortOrder } = action.payload ?? {};
        // 화면은 limit/offset 을 쓰고 API 는 page/size 를 쓴다. 여기서 바꿔 준다.
        const size = limit && limit > 0 ? limit : 20;
        const params = new URLSearchParams({
            page: String(Math.floor((offset || 0) / size)),
            size: String(size),
        });
        if (keyword && keyword.trim()) params.append('keyword', keyword.trim());
        // 정렬을 빠뜨리면 헤더 아이콘만 바뀌고 순서는 그대로다. 실제로 그 상태였다.
        if (sortBy) params.append('sortBy', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);

        const response = yield call(() =>
            api.get(`/partners/clients?${params}`)
        );

        if (response.data.resultCode === "0000") {
            const page = response.data.data ?? {};
            yield put(client.fetchClientSuccess({
                data: page.items ?? [], totalCount: page.total ?? 0,
                headquarters: page.headquarters ?? null,
            }));
        } else {
            yield put(client.fetchClientFailure(response.data.error));
        }

    } catch (error) {
        yield put(client.fetchClientFailure(error.message));
    }
}

function* fetchClientDataList(action) {
    try {
        const response = yield call(() =>
            api.get('/partners/clients/options')
        );

        if (response.data.resultCode === "0000") {
            yield put(client.fetchClientListSuccess({ data: response.data.data }));
        } else {
            yield put(client.fetchClientListFailure(response.data.error));
        }

    } catch (error) {
        yield put(client.fetchClientListFailure(error.message));
    }
}

function* fetchAddClient(action) {
    // 실패하면 지금 보고 있는 페이지를 그대로 다시 받는다. 페이지 위치는 화면이 들고 있어서,
    // 다른 페이지를 받아 오면 화면의 쪽 번호와 행이 어긋난다.
    const { limit, offset, keyword, sortBy, sortOrder } = yield select(state => state.client);
    const reload = fetchClientRequest({ limit, offset, keyword, sortBy, sortOrder });
    try {
        // WEBCOM-5519: payload 가 string 이면 비파트너 (backward compat),
        // object 이면 Partner 메타데이터 포함된 신규 형식.
        const payload = action.payload;
        const body = (typeof payload === 'string')
            ? { clientName: payload }
            : {
                clientName:     payload.clientName,
                contactEmail:   payload.contactEmail   || null,
                customerType:   payload.customerType   || null,
                partnerType:    payload.partnerType    || null,
                tier:           payload.tier           || null,
                discountRate:   payload.discountRate   ?? null,
                invoiceStatus:  payload.invoiceStatus  || null,
                // 본사 표시. 빠져 있어서 체크해도 서버에 닿지 않았고, 그래서 그
                // 고객사의 계약이 Partner 현황 카드에 그대로 잡혔다.
                headquarters:   payload.headquarters   ?? null,
            };

        const response = yield call(() =>
            api.post('/partners/clients', body, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })
        );

        if (response.data.resultCode === "0000") {
            // 화면이 새 고객사가 보이는 첫 페이지로 옮긴 뒤 다시 받는다. 여기서 받으면 화면이
            // 옮기기 전의 페이지를 받아 와 한 번 더 받게 된다.
            const newId = response.data.data?.clientId ?? null;
            // 사내 계정 표시는 등록 API 가 받지 않는다(본사만 부르는 자리를 따로 둔다). 등록이
            // 된 뒤에 건다. 실패해도 고객사는 이미 있으므로 등록은 되돌리지 않는다.
            const internalError = (typeof payload === 'object' && payload.internal && newId)
                ? yield* setInternal(newId, true)
                : null;
            yield put(client.fetchAddClientSuccess({
                clientId: newId,
            }));
            // 알림은 하나만 띄운다. 알림은 마지막 것만 남으므로(takeLatest), 실패를 먼저 띄우고
            // 성공을 이어 띄우면 실패가 지워져 사용자는 사내 계정까지 지정된 줄 안다.
            yield put(message.fetchMessageBoxRequest(internalError !== null
                ? partialFailure('client_msg.registeredButInternalFailed', internalError)
                : {
                    isError: false,
                    isSuccess: true,
                    resultCode: response.data.resultCode,
                    message: i18next.t('client_msg.registerSuccess'),
                }));
        } else {
            const msg = resolveError(response.data) || i18next.t('client_msg.registerFailed');
            yield put(client.fetchAddClientFailure(msg));
            yield put(
                message.fetchMessageBoxRequest({
                    isError: true,
                    isSuccess: false,
                    resultCode: response.data.resultCode,
                    message: msg,
                })
            );
            yield put(reload);
        }

    } catch (error) {
        const addErrMsg = error.response?.status === 401
            ? i18next.t('common.sessionExpired')
            : i18next.t('common.unexpectedError');
        yield put(client.fetchAddClientFailure(addErrMsg));
        yield put(
            message.fetchMessageBoxRequest({
                isError: true,
                isSuccess: false,
                resultCode: '9999',
                message: addErrMsg,
            })
        );
        yield put(reload);
    } finally {
        yield put(client.setLoading(false));
    }
}

function* fetchUpdateClient(action) {
    try {
        const { clientId, isDisplay } = action.payload;

        // 고객사를 지우는 기능은 없다. 발급 원장이 client_id 를 값으로만 갖고 있어
        // 지우면 이미 발급된 라이선스의 소속이 사라진다. 숨김 처리만 한다.
        const response = yield call(() =>
            api.patch(`/partners/clients/${clientId}/display`,
                { display: isDisplay === 'Y' || isDisplay === true })
        );

        if (response.data.resultCode === "0000") {
            yield put(client.fetchUpdateClientSuccess({ clientId, isDisplay }));
            yield put(
                message.fetchMessageBoxRequest({
                    isError: false,
                    isSuccess: true,
                    resultCode: response.data.resultCode,
                    message: resolveError(response.data) || i18next.t('client_msg.updateSuccess'),
                })
            );
        } else {
            yield put(client.fetchUpdateClientFailure(response.data.resultMessage));
            yield put(
                message.fetchMessageBoxRequest({
                    isError: true,
                    isSuccess: false,
                    resultCode: response.data.resultCode,
                    message: resolveError(response.data) || i18next.t('client_msg.updateFailed'),
                })
            );
        }

    } catch (error) {
        const status = error.response?.status;
        const updateErrMsg = status === 401
            ? i18next.t('common.sessionExpired')
            : status === 403
                ? i18next.t('common.forbidden')
                : i18next.t('common.unexpectedError');
        yield put(client.fetchUpdateClientFailure(updateErrMsg));
        yield put(
            message.fetchMessageBoxRequest({
                isError: true,
                isSuccess: false,
                resultCode: '9999',
                message: updateErrMsg,
            })
        );
    } finally {
        yield put(client.setLoading(false));
    }
}

/**
 * 파트너 메타 수정.
 *
 * 여기 있는 값은 고객사의 **현재** 등급이다. 발급 시점의 조건이 필요하면 발급할 때
 * tb_license_portal 에 복사되므로, 등급을 바꿔도 이미 발급된 라이선스에 소급되지 않는다.
 */
/**
 * 사내 계정 표시를 바꾼다.
 *
 * 알림은 여기서 띄우지 않는다. 부르는 쪽이 저장 결과와 합쳐 하나만 띄운다 - 여기서 실패를
 * 알려도 뒤이은 저장 성공 알림이 덮는다.
 *
 * @return 성공하면 null, 실패하면 이유(서버가 준 안내. 없으면 빈 문자열)
 */
function* setInternal(clientId, internal) {
    try {
        const res = yield call(() => api.patch(`/partners/clients/${clientId}/internal`, { internal }));
        if (res.data?.resultCode === '0000') return null;
        return resolveError(res.data) || '';
    } catch (e) {
        return resolveError(e.response?.data) || '';
    }
}

/** 고객사는 저장했지만 사내 계정 표시는 바꾸지 못했다는 오류 알림. 이유가 있으면 덧붙인다. */
function partialFailure(messageKey, reason) {
    const base = i18next.t(messageKey);
    return {
        isError: true,
        isSuccess: false,
        resultCode: '9999',
        message: reason ? `${base} (${reason})` : base,
    };
}

function* fetchUpdateClientMeta(action) {
    try {
        const { clientId, contactEmail, customerType, partnerType, tier,
                discountRate, invoiceStatus, headquarters } = action.payload;

        // 이름은 보내지 않는다. 원장의 주인이 발급 서버이고 같은 번호에 다른 이름을 주면
        // 거부당한다 - 이미 발급된 라이선스에 실린 이름과 어긋나기 때문이다.
        const body = {
            contactEmail:  contactEmail  ?? null,
            customerType:  customerType  ?? null,
            partnerType:   partnerType   ?? null,
            tier:          tier          ?? null,
            discountRate:  discountRate  ?? null,
            invoiceStatus: invoiceStatus ?? null,
            // 보내지 않으면 서버가 지금 표시를 건드리지 않는다. 다른 항목만 고치는
            // 요청이 본사 표시를 지우지 않게 한 규칙이라, null 을 그대로 보낸다.
            headquarters:  headquarters ?? null,
        };

        const response = yield call(() =>
            api.patch(`/partners/clients/${clientId}`, body, {
                headers: { 'Content-Type': 'application/json' },
            })
        );

        if (response.data.resultCode === '0000') {
            // 사내 계정 표시는 메타를 고친 뒤에 건다. 유형을 파트너로 바꾸면서 켜는 경우, 유형이
            // 먼저 저장돼야 서버가 받아들인다.
            const internalError = typeof action.payload.internal === 'boolean'
                ? yield* setInternal(clientId, action.payload.internal)
                : null;
            yield put(client.fetchUpdateClientMetaSuccess());
            // 본사 표시를 옮기면 등록 모달이 막는 기준도 바뀐다. 다시 받지 않으면 해제한
            // 뒤에도 체크박스가 잠겨 있다.
            const { limit, offset, keyword, sortBy, sortOrder } = yield select(state => state.client);
            yield put(fetchClientRequest({ limit, offset, keyword, sortBy, sortOrder }));
            // 등록과 같다 - 알림은 하나만. 사내 계정이 실패했으면 그것을 남긴다.
            yield put(message.fetchMessageBoxRequest(internalError !== null
                ? partialFailure('client_msg.savedButInternalFailed', internalError)
                : {
                    isError: false,
                    isSuccess: true,
                    resultCode: response.data.resultCode,
                    message: resolveError(response.data) || i18next.t('client_msg.metaUpdateSuccess'),
                }));
        } else {
            yield put(client.fetchUpdateClientMetaFailure(response.data.resultMessage));
            yield put(message.fetchMessageBoxRequest({
                isError: true,
                isSuccess: false,
                resultCode: response.data.resultCode,
                message: resolveError(response.data) || i18next.t('client_msg.metaUpdateFailed'),
            }));
        }
    } catch (error) {
        const msg = error.response?.status === 401
            ? i18next.t('common.sessionExpired')
            : (resolveError(error.response?.data) || i18next.t('common.unexpectedError'));
        yield put(client.fetchUpdateClientMetaFailure(msg));
        yield put(message.fetchMessageBoxRequest({
            isError: true,
            isSuccess: false,
            resultCode: '9999',
            message: msg,
        }));
    } finally {
        yield put(client.setLoading(false));
    }
}